import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, SchoolLevel } from '@jeraisonne/db';
import type { DailyExerciseResult } from './objective-types';

type ExerciseRow = {
  id: string;
  title: string;
  difficulty: string;
  estimated_minutes: number;
  chapter_id: string;
  illustration_url: string | null;
  chapters: { title: string; subject: string; illustration_url: string | null } | null;
  attempts: { id: string }[];
};

/**
 * Returns today's exercise for the given level.
 * First checks admin-scheduled daily_exercises table.
 * Falls back to the most-attempted published exercise for that level.
 */
export async function getDailyExercise(
  supabase: SupabaseClient<Database>,
  level: SchoolLevel,
): Promise<DailyExerciseResult | null> {
  const today = new Date().toISOString().slice(0, 10);

  // Step 1: admin-scheduled entry
  const { data: scheduled } = await supabase
    .from('daily_exercises')
    .select(
      `
      exercise_id,
      exercises (
        id, title, difficulty, estimated_minutes, chapter_id, illustration_url,
        chapters ( title, subject, illustration_url )
      )
    `,
    )
    .eq('level', level)
    .eq('date', today)
    .eq('is_active', true)
    .maybeSingle();

  if (scheduled?.exercises) {
    const ex = scheduled.exercises as unknown as ExerciseRow;
    return {
      exerciseId: ex.id,
      title: ex.title,
      difficulty: ex.difficulty as DailyExerciseResult['difficulty'],
      estimatedMinutes: ex.estimated_minutes,
      chapterId: ex.chapter_id,
      chapterTitle: ex.chapters?.title ?? '',
      subject: (ex.chapters?.subject ?? 'math') as DailyExerciseResult['subject'],
      source: 'scheduled',
      illustrationUrl: ex.illustration_url ?? ex.chapters?.illustration_url ?? null,
    };
  }

  // Step 2: fallback — most-attempted published exercise for this level
  const { data: candidates } = await supabase
    .from('exercises')
    .select(
      `
      id, title, difficulty, estimated_minutes, chapter_id, illustration_url,
      chapters!inner ( title, subject, level, illustration_url ),
      attempts ( id )
    `,
    )
    .eq('status', 'published')
    .eq('chapters.level', level)
    .limit(50);

  if (!candidates || candidates.length === 0) return null;

  const typedCandidates = candidates as unknown as ExerciseRow[];
  const ranked = [...typedCandidates].sort((a, b) => {
    const ca = Array.isArray(a.attempts) ? a.attempts.length : 0;
    const cb = Array.isArray(b.attempts) ? b.attempts.length : 0;
    return cb - ca;
  });

  const best = ranked[0]!;

  return {
    exerciseId: best.id,
    title: best.title,
    difficulty: best.difficulty as DailyExerciseResult['difficulty'],
    estimatedMinutes: best.estimated_minutes,
    chapterId: best.chapter_id,
    chapterTitle: best.chapters?.title ?? '',
    subject: (best.chapters?.subject ?? 'math') as DailyExerciseResult['subject'],
    source: 'fallback',
    illustrationUrl: best.illustration_url ?? best.chapters?.illustration_url ?? null,
  };
}
